import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { useApi } from '@/lib/auth'
import { usePreviewData } from '../preview-data'
import { okSchema, revealSchema, type VendorKey, type VendorSettings, vendorSettingsSchema } from './vendors'

// GET /v1/admin/org/settings -> { success, data: { meraki?: {...masked, enabled}, ... } }
const settingsEnvelopeSchema = z.object({ success: z.boolean(), data: vendorSettingsSchema })

export const adminCredentialsQueryKey = ['admin', 'credentials'] as const

/** The caller's org vendor credentials (secrets masked). Use `vendorBlock` / `VENDOR_FIELDS` from `./vendors`. */
export function useAdminCredentials() {
  const api = useApi()
  const preview = usePreviewData()
  return useQuery({
    queryKey: preview ? ['preview', 'admin-credentials'] : adminCredentialsQueryKey,
    queryFn: async (): Promise<VendorSettings> => {
      const res = await api.get('/v1/admin/org/settings', settingsEnvelopeSchema)
      if (!res.success) throw new Error('Failed to fetch credentials')
      return res.data
    },
    enabled: !preview,
    ...(preview ? { initialData: preview.adminCredentials, staleTime: Number.POSITIVE_INFINITY } : {}),
  })
}

/**
 * Save edited fields OR toggle enabled. `mutate({ vendor, data })` PATCH /v1/admin/org/settings.
 * Build `data` with `vendorPatchBody(vendor, edits)` / `vendorToggleBody(vendor, enabled)` from `./vendors`.
 */
export function useUpdateCredentials() {
  const api = useApi()
  const qc = useQueryClient()
  const preview = usePreviewData()
  return useMutation({
    mutationFn: async (v: { vendor: VendorKey; data: Record<string, unknown> }) => {
      if (preview) return { success: true }
      return api.request('/v1/admin/org/settings', { method: 'PATCH', body: v }, okSchema)
    },
    onSuccess: async () => {
      if (!preview) await qc.invalidateQueries({ queryKey: adminCredentialsQueryKey })
    },
  })
}

/**
 * Unmask one secret. `mutateAsync({ vendor, field })` -> `string` (POST /v1/admin/org/settings/reveal; a read, audit-sensitive).
 * The caller owns the 30s auto-mask timer; never cache or persist the value (this hook does not touch the query cache).
 */
export function useRevealCredential() {
  const api = useApi()
  const preview = usePreviewData()
  return useMutation({
    mutationFn: async (v: { vendor: VendorKey; field: string }): Promise<string> => {
      if (preview) return 'preview-secret-value'
      const res = await api.request('/v1/admin/org/settings/reveal', { method: 'POST', body: v }, revealSchema)
      return res.data.value
    },
    gcTime: 0,
  })
}
